import { useCallback, useMemo } from 'react';
import { PINNED_KEYS } from '../utils/columns';
import { reportStorageKey } from './storageKeys';
import { usePersistentState } from './usePersistentState';

const MIN_WIDTH = 84;
const MAX_WIDTH = 640;

// `columnOrder` is the camp's default order (fixed columns, then its question columns). A stored
// order drops columns the camp no longer has and appends new ones at the end.
export function useTableLayout(campCode, columnOrder) {
  const [storedOrder, setStoredOrder] = usePersistentState(reportStorageKey(campCode, 'columnOrder'), columnOrder);
  const [hidden, setHidden] = usePersistentState(reportStorageKey(campCode, 'hiddenColumns'), []);
  const [widths, setWidths] = usePersistentState(reportStorageKey(campCode, 'columnWidths'), {});
  const [density, setDensity] = usePersistentState(reportStorageKey(campCode, 'density'), 'comfortable');

  const order = useMemo(() => {
    const movable = storedOrder.filter(k => columnOrder.includes(k) && !PINNED_KEYS.includes(k));
    const missing = columnOrder.filter(k => !PINNED_KEYS.includes(k) && !movable.includes(k));
    return [...PINNED_KEYS, ...movable, ...missing];
  }, [storedOrder, columnOrder]);

  const visibleKeys = useMemo(() => order.filter(k => k === 'participant' || !hidden.includes(k)), [order, hidden]);

  const toggleColumn = useCallback(
    key => {
      if (key === 'participant') return;
      setHidden(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    },
    [setHidden]
  );

  const showAllColumns = useCallback(() => setHidden([]), [setHidden]);
  const hideAllColumns = useCallback(() => setHidden(columnOrder.filter(k => k !== 'participant')), [setHidden, columnOrder]);

  const moveColumn = useCallback(
    (from, to) => {
      if (from === to || PINNED_KEYS.includes(from) || PINNED_KEYS.includes(to)) return;
      const fromIndex = order.indexOf(from);
      const toIndex = order.indexOf(to);
      const next = order.filter(k => k !== from);
      const target = next.indexOf(to);
      next.splice(fromIndex < toIndex ? target + 1 : target, 0, from);
      setStoredOrder(next);
    },
    [order, setStoredOrder]
  );

  const resizeColumn = useCallback(
    (key, width) => {
      setWidths(prev => ({ ...prev, [key]: Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width))) }));
    },
    [setWidths]
  );

  const resetColumnWidth = useCallback(
    key => {
      setWidths(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [setWidths]
  );

  const resetLayout = useCallback(() => {
    setStoredOrder(columnOrder);
    setHidden([]);
    setWidths({});
  }, [setStoredOrder, setHidden, setWidths, columnOrder]);

  const applyViewLayout = useCallback(
    state => {
      setHidden(columnOrder.filter(k => k !== 'participant' && !state.visibleKeys.includes(k)));
      setDensity(state.density);
    },
    [setHidden, setDensity, columnOrder]
  );

  const isCustomized = Object.keys(widths).length > 0 || order.some((k, i) => k !== columnOrder[i]);

  return {
    order, visibleKeys, widths, density, setDensity, isCustomized,
    toggleColumn, showAllColumns, hideAllColumns, moveColumn, resizeColumn, resetColumnWidth, resetLayout, applyViewLayout,
  };
}
